import React, { useState, useEffect, useCallback } from "react";
import { Typography, Grid, Button, Tooltip, Box, DialogContentText } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import Header from "../../Header/Header";
import CustomerSelector from "../../Common/CustomerSelector";
import SiteSelector from "../../Common/SiteSelector";
import ProductSelectList from "../../Common/Selectors/ProductSelectList";
import CustomerCard from "../../Common/CustomerCard";
import provisionService from "../../../services/ProvisionService";
import { useTheme } from "@mui/material/styles";
import { getGenericStyles } from "../../../theme/styles";
import AcademicYearSelector from "../../Common/AcademicYearSelector";
import {
  ProductDTO,
  AcademicYearDTO,
  SiteDTO,
  ProvisionedAuditLog,
} from "@beable/roster-backend-client-v1";
import BasePanel from "../../Common/Layout/BasePanel";
import { BasePanelBox } from "../../Common/Layout/BasePanelBox";
import { StyledPanelHeader } from "../../Common/Layout/PanelHeader";
import BaseSheet from "../../Common/Layout/BaseSheet";
import { formatLogText } from '../../../util/formatLogText';
import { CustomerDTO, ProvisionTreeNodeDTO, ProvisionAction } from "@beable/roster-backend-client-v1";
import ProvisionTreeSelections from "../../ProvisionTreeSelections/ProvisionTreeSelections";
import ConfirmRevoke from "./ConfirmRevoke";
import BaseDialog from "../../Common/BaseDialog";

export default function RevokeProvision() {
  const theme = useTheme();
  const styles = getGenericStyles(theme);

  const [customer, setCustomer] = useState<CustomerDTO>();
  const [site, setSite] = useState<SiteDTO>();
  const [academicYear, setAcademicYear] = useState<AcademicYearDTO>();
  const [product, setProduct] = useState<ProductDTO>();

  const [rosterWarning, setRosterWarning] = useState("");
  const [nodes, setNodes] = useState<any[]>([]);
  const [checkedNodes, setCheckedNodes] = useState<any[]>([]);
  const [preSelectedNodes, setPreSelectedNodes] = useState<any[]>([]);
  const [onlyPrecheckedNodes, setOnlyPrecheckedNodes] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isNodesLoading, setIsNodesLoading] = useState(false);
  const [logInfo, setLogInfo] = useState<ProvisionedAuditLog | undefined>();
  const [loadingProgress, setLoadingProgress] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [hasResult, setHasResult] = useState(false);

  const clearNodes = () => {
    setNodes([]);
    setCheckedNodes([]);
    setPreSelectedNodes([]);
    setOnlyPrecheckedNodes([])
  }

  const handleCustomerChange = (customer: CustomerDTO | undefined) => {
    resetSelections();
    setCustomer(customer);
    clearNodes();
  }

  const resetSelections = () => {
    setSite(undefined);
    setAcademicYear(undefined);
  }

  const handleSiteChange = (site: SiteDTO | undefined) => {
    clearNodes();
    setAcademicYear(undefined);
    setSite(site);
  }

  const handleNodeCheck = (data: any[]) => {
    setCheckedNodes([...data]);
    setPreSelectedNodes([...data]);
  };

  const handleProductChange = (product: ProductDTO) => {
    clearNodes();
    setProduct(product);
  }

  const handleAcademicYearChange = (academicYear: AcademicYearDTO | undefined) => {
    clearNodes();
    setAcademicYear(academicYear);
  }

  async function getNodeChildren(node: ProvisionTreeNodeDTO) {
    const data = await provisionService.getProvisionNodes(site!.id, academicYear!.academicYearId, node.value);
    return data;
  }

  async function getRootNode(node: ProvisionTreeNodeDTO, appendChildren?: boolean) {
    const data = await provisionService.getRootNode(site!.id, academicYear!.academicYearId, node.value, appendChildren);
    return data;
  }

  useEffect(() => {
    let valueOfNodes: any[];
    let valueOfSelections: any[];
    const fetchNodeData = async () => {
      const roots = await provisionService.getProvisionNodes(site!.id, academicYear!.academicYearId);
      let data;

      if (roots?.length) {
        const progressUnit = 100 / roots.length;
        let progress = 0;
        for (const root of roots) {
          const rootWithChildren = await provisionService.getRootNode(site!.id, academicYear!.academicYearId, root.value, true);
          root.children = rootWithChildren?.children;
          progress += progressUnit;
          setLoadingProgress(`${progress.toFixed(2)}%`);
        }
        data = roots;
      }
      setLoadingProgress('');

      if (data && data[0] && data[0].warning) {
        setRosterWarning(data[0].warning);
      } else if (!data) {
        setRosterWarning(
          "Error while getting the roster data. Please contact our support..."
        );
      } else if (data) {
        valueOfNodes = data;
        setNodes(data);
        setRosterWarning("");
      }
    };

    const fetchSelectionData = async () => {
      const data = await provisionService.getPreSelectedNodes(site!.id, academicYear!.academicYearId, product!.id);
      if (data) {
        // TODO: remove this check. Added for backwards compatability
        if (Array.isArray(data)) {
          setCheckedNodes(data);
          setPreSelectedNodes(data);
          return;
        }
        valueOfSelections = data.selections;
        setCheckedNodes(data.selections);
        setPreSelectedNodes(data.selections);
        setLogInfo(data.log)
      }
    };

    const getPreselectedNodes = (nodes: Node[], preSelectedNodeValues: string[]) => {
      if (!nodes || !preSelectedNodeValues) { return; }
      const preselected = nodes.reduce(filterReturnSelectedNodes, []);
      setOnlyPrecheckedNodes(preselected);
      function filterReturnSelectedNodes(filtered: any, node: any) {
        const children = (node.children || []).reduce(filterReturnSelectedNodes, []);
        if
          // Node's value matches a preselected value
          (preSelectedNodeValues.includes(node.value) ||
          // Or a children has a matching node
          children.length
        ) {
          if (children.length) {
            filtered.push({ ...node, children });
          } else {
            filtered.push({ ...node });
          }
        }
        return filtered;
      }
    }

    const fetchData = async () => {
      setIsNodesLoading(true);
      await fetchSelectionData();
      await fetchNodeData();
      getPreselectedNodes(valueOfNodes, valueOfSelections)
      setIsNodesLoading(false);
    }

    if (site?.id && academicYear?.academicYearId && product?.id) {
      fetchData();
    }
  }, [site, academicYear, product]);

  const revokeForAcademicYear = async () => {
    setDialogOpen(false);
    setIsLoading(true);
    const data = await provisionService.saveSelectedNodes(site!.id, academicYear!.academicYearId, product!.id, checkedNodes);
    if (data) {
      const provisionData = await provisionService.provisionAcademicYear(site!.id, academicYear!.academicYearId, product!.id, ProvisionAction.Revoke);
      if (provisionData) {
        setIsLoading(false);
        setResultMessage('Successfully began the revoking process.');
        setHasResult(true);
      } else {
        setIsLoading(false);
        setResultMessage('There was an error revoking the records.');
        setHasResult(true);
      }
    }
    else {
      setIsLoading(false);
      setResultMessage('There was an error updating the selections.');
      setHasResult(true);

    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const resetResult = () => {
    setResultMessage('');
    setHasResult(false);
  }

  const memoHandleSelectionsChange = useCallback(handleNodeCheck, [checkedNodes, preSelectedNodes]);

  return (
    <>
      <Header />
      <Grid container>
        <Box
          component="form"
          sx={styles.root}
          data-testid="test-revokeForm"
          autoComplete="off"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
          }}
        >
          <BasePanel>
            <BasePanelBox>
              <StyledPanelHeader header="Revoke Users" />
              <Grid container size={12}>
                <CustomerSelector
                  onSelect={handleCustomerChange}
                />
                <br />
                <SiteSelector
                  customerId={customer?.id ?? ''}
                  onSelect={handleSiteChange}
                />
                <br />
                <ProductSelectList
                  productId={product?.id ?? ''}
                  customerId={customer?.id ?? ''}
                  siteId={site?.id ?? 0}
                  onChange={handleProductChange}
                />
                <br />
                <AcademicYearSelector
                  siteId={site ? site.id : 0}
                  handleSelection={handleAcademicYearChange}
                  clearAcademicYear={!academicYear}
                  disabled={!site}
                />
                <br />
                <Grid sx={{ marginLeft: theme.spacing(2) }} size={11}>
                  <CustomerCard customer={customer} />
                </Grid>
                <br />
                <br />
              </Grid>
            </BasePanelBox>
          </BasePanel>
          <BaseSheet id="revoke-users-container" maxWidth="lg">
            <Grid size={12}>
              <Typography variant="h6" sx={styles.stepContainer_header}>
                Revoke Provisioned Users
                {logInfo && <Tooltip title={formatLogText(logInfo)}>
                  <InfoIcon color="primary" sx={styles.infoIcon} />
                </Tooltip>}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={styles.customerCard_content}
              >
                The displayed orgs have current provisioning selections. <br />
                Revoking these selections will cause all revoked users to be removed from all reports.
                Use this page carefully.<br />
                <br />
                To revoke users:<br />
                Deselect the data that was provisioned in error.<br />
                • All the data that is selected will remain provisioned.<br />
                • The data that is NOT selected will be revoked.<br />
              </Typography>
              <ProvisionTreeSelections
                nodes={nodes}
                preSelectedNodeValues={preSelectedNodes}
                onSelectionsChange={(newSelectedNodes) => memoHandleSelectionsChange(newSelectedNodes)}
                getNodeChildren={getNodeChildren}
                getRootNode={getRootNode}
                rosterWarning={rosterWarning}
                isLoading={isNodesLoading}
                loadingProgress={loadingProgress}
                precheckedNodes={onlyPrecheckedNodes}
              />
              <Grid
                container
                spacing={2}
                sx={[{
                  justifyContent: "center"
                }, styles.grow]}>
                <Grid
                  key="0"
                  size={{
                    xs: 8,
                    sm: 4
                  }}>
                  <Tooltip
                    title={
                      `Revoking users will remove them from all reports. There is no way to undo revoking. 
                        Please ensure that revoking is the desired action. If you'd like to simply deprovision users,
                        please navigate to Provision > Users`
                    }
                  >
                    <div>
                      <Button
                        variant={"contained"}
                        color={"secondary"}
                        sx={styles.checkboxSubmit}
                        onClick={() => setDialogOpen(true)}
                        disabled={
                          !site?.id ||
                          !academicYear?.academicYearId ||
                          rosterWarning.length > 0 ||
                          isLoading
                        }
                      >
                        Revoke
                      </Button>
                    </div>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </BaseSheet>
        </Box>
      </Grid>
      {dialogOpen &&
        <ConfirmRevoke
          closeDialog={closeDialog}
          revoke={revokeForAcademicYear}
        ></ConfirmRevoke>}
      <BaseDialog
        open={hasResult}
        showCloseIcon={false}
        showCloseButton={true}
        onClose={() => resetResult()}
        maxWidth='xs'
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center"
          }}>
          <Box sx={{
            width: "100%"
          }}>
            <DialogContentText>
              {resultMessage}
            </DialogContentText>
          </Box>
        </Box>
      </BaseDialog>
    </>
  );
}
